import { debug } from '../../utils/debug';
import React, { useState, useMemo, useEffect } from 'react';
import { m, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiX, FiPlusCircle, FiShield, FiMap, FiSmartphone, FiAward, FiUsers, FiPlus, FiMinus } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { useCurrency } from '../../context/CurrencyContext';
import { getProductTranslation } from '../../utils/translations';
import { hexToRgba } from '../../utils/colors';
import { getValidPricingRules, findMatchingPricingRule } from '../../utils/pricingRules';
import { useMotionConfig } from '../../hooks/useReducedMotion';
import { useFocusTrap } from '../../hooks/useFocusTrap';

const addonIcons = {
  insurance: FiShield,
  basic_insurance: FiShield,
  gps: FiMap,
  phone_holder: FiSmartphone,
  phone_mount: FiSmartphone,
  premium_helmet: FiAward,
  child_seat: FiUsers,
  child_seat_addon: FiUsers,
};

const AddonsModal = ({ isOpen, onClose, selectedProducts, onContinue, initialAddons, addonsData, duration, startDate, flowType }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { currentLanguage, defaultLanguage } = useLanguage();
  const motion = useMotionConfig();
  const focusTrapRef = useFocusTrap(isOpen, onClose);
  const { formatCurrency } = useCurrency();
  const [selectedAddons, setSelectedAddons] = useState({});

  // Helper function to get dynamic price for an addon based on selected duration
  const getAddonPrice = (addon) => {
    if (!addon) {
      debug.warn('⚠️ [AddonsModal] getAddonPrice called with undefined addon');
      return 0;
    }
    const allPricingRules = addon._pricingRules || [];
    const activePricingRules = getValidPricingRules(allPricingRules, startDate, { flowType });

    const durationId = duration?.id;
    if (!durationId || activePricingRules.length === 0) {
      return parseFloat(addon.price || 0);
    }

    const pricingRule = findMatchingPricingRule(activePricingRules, durationId);
    return pricingRule ? parseFloat(pricingRule.price) : parseFloat(addon.price || 0);
  };

  const totalBikes = useMemo(() => {
    if (!selectedProducts) return 0;
    return selectedProducts.reduce((total, product) => total + product.quantity, 0);
  }, [selectedProducts]);

  useEffect(() => {
    if (initialAddons) {
      const addonQuantities = {};
      initialAddons.forEach(addon => {
        addonQuantities[addon.id] = addon.quantity || 1;
      });
      setSelectedAddons(addonQuantities);
    }
  }, [initialAddons]);

  const availableAddons = useMemo(() => {
    debug.log('🔍 [AddonsModal] availableAddons calculation:', {
      hasSelectedProducts: !!selectedProducts,
      selectedProductsCount: selectedProducts?.length,
      hasAddonsData: !!addonsData,
      addonsDataKeys: Object.keys(addonsData || {})
    });

    if (!selectedProducts || !addonsData) {
      debug.log('🔍 [AddonsModal] Returning empty - missing selectedProducts or addonsData');
      return [];
    }

    const addonIds = new Set();
    selectedProducts.forEach(product => {
      product.availableAddons?.forEach(addonId => {
        addonIds.add(addonId);
      });
    });

    debug.log('🔍 [AddonsModal] Product addon IDs collected:', Array.from(addonIds));

    // If no product-specific add-ons found (e.g., for bundles), show all location add-ons
    // but still filter by pre-booking requirements
    if (addonIds.size === 0 && addonsData) {
      debug.log('🔍 [AddonsModal] No product-specific addons, checking all location addons:', Object.keys(addonsData));

      const allLocationAddons = Object.values(addonsData);

      // Skip pre-booking filter for manage flow (booking already exists)
      if (flowType === 'manage') {
        debug.log('🔍 [AddonsModal] Manage flow - skipping pre-booking filter for location addons');
        return allLocationAddons;
      }

      // Filter by pre-booking hours (if startDate is available)
      if (!startDate) {
        debug.warn('🔍 [AddonsModal] No startDate provided, skipping pre-booking filter for location addons');
        return allLocationAddons;
      }

      const now = new Date();
      const bookingStartDate = new Date(startDate);

      // Check if startDate is valid
      if (isNaN(bookingStartDate.getTime())) {
        debug.warn('🔍 [AddonsModal] Invalid startDate provided:', startDate, '- skipping pre-booking filter');
        return allLocationAddons;
      }

      const hoursUntilStart = (bookingStartDate - now) / (1000 * 60 * 60);

      // For immediate bookings where startDate is in the past or very recent,
      // treat as "booking now" and only filter add-ons requiring advance notice
      const effectiveHoursUntilStart = Math.max(0, hoursUntilStart);
      const isImmediateBooking = hoursUntilStart < 0.1; // Less than 6 minutes

      debug.log('🔍 [AddonsModal] Pre-booking filter for location addons:', {
        now: now.toISOString(),
        startDate: bookingStartDate.toISOString(),
        hoursUntilStart: hoursUntilStart.toFixed(2),
        effectiveHoursUntilStart: effectiveHoursUntilStart.toFixed(2),
        isImmediateBooking
      });

      const filteredLocationAddons = allLocationAddons.filter(addon => {
        // Check both camelCase and snake_case field names
        const requiredHours = addon.preBookingHours || addon.pre_booking_hours || 0;
        const meetsRequirement = effectiveHoursUntilStart >= requiredHours;

        debug.log(`🔍 [AddonsModal] Checking addon "${addon.name}":`, {
          preBookingHours: addon.preBookingHours,
          pre_booking_hours: addon.pre_booking_hours,
          requiredHours,
          effectiveHoursUntilStart: effectiveHoursUntilStart.toFixed(2),
          meetsRequirement
        });

        if (!meetsRequirement) {
          debug.log(`🔍 [AddonsModal] ❌ Filtering out location addon "${addon.name}" - requires ${requiredHours}h advance, booking in ${effectiveHoursUntilStart.toFixed(2)}h`);
        }

        return meetsRequirement;
      });

      debug.log('🔍 [AddonsModal] Filtered location addons:', filteredLocationAddons.length, 'of', allLocationAddons.length);
      return filteredLocationAddons;
    }

    const mappedAddons = Array.from(addonIds).map(id => {
      const addon = addonsData[id];
      debug.log(`🔍 [AddonsModal] Mapping addon ID ${id}:`, addon ? 'FOUND' : 'NOT FOUND in addonsData');
      return addon;
    }).filter(Boolean);

    debug.log('🔍 [AddonsModal] Mapped product-specific addons:', mappedAddons.length);

    // Skip pre-booking filter for manage flow (booking already exists)
    if (flowType === 'manage') {
      debug.log('🔍 [AddonsModal] Manage flow - skipping pre-booking filter for product addons');
      return mappedAddons;
    }

    // Filter out add-ons that don't meet pre-booking time requirements
    if (!startDate) {
      debug.warn('🔍 [AddonsModal] No startDate provided, skipping pre-booking filter for product addons');
      return mappedAddons;
    }

    const now = new Date();
    const bookingStartDate = new Date(startDate);

    // Check if startDate is valid
    if (isNaN(bookingStartDate.getTime())) {
      debug.warn('🔍 [AddonsModal] Invalid startDate provided:', startDate, '- skipping pre-booking filter');
      return mappedAddons;
    }

    const hoursUntilStart = (bookingStartDate - now) / (1000 * 60 * 60);

    // For immediate bookings where startDate is in the past or very recent,
    // treat as "booking now" and only filter add-ons requiring advance notice
    const effectiveHoursUntilStart = Math.max(0, hoursUntilStart);
    const isImmediateBooking = hoursUntilStart < 0.1; // Less than 6 minutes

    debug.log('🔍 [AddonsModal] Pre-booking filter for product addons:', {
      now: now.toISOString(),
      startDate: bookingStartDate.toISOString(),
      hoursUntilStart: hoursUntilStart.toFixed(2),
      effectiveHoursUntilStart: effectiveHoursUntilStart.toFixed(2),
      isImmediateBooking
    });

    const filteredAddons = mappedAddons.filter(addon => {
      // Check both camelCase and snake_case field names
      const requiredHours = addon.preBookingHours || addon.pre_booking_hours || 0;
      const meetsRequirement = effectiveHoursUntilStart >= requiredHours;

      debug.log(`🔍 [AddonsModal] Checking addon "${addon.name}":`, {
        preBookingHours: addon.preBookingHours,
        pre_booking_hours: addon.pre_booking_hours,
        requiredHours,
        effectiveHoursUntilStart: effectiveHoursUntilStart.toFixed(2),
        meetsRequirement
      });

      if (!meetsRequirement) {
        debug.log(`🔍 [AddonsModal] ❌ Filtering out addon "${addon.name}" - requires ${requiredHours}h advance, booking in ${effectiveHoursUntilStart.toFixed(2)}h`);
      }

      return meetsRequirement;
    });

    debug.log('🔍 [AddonsModal] Final availableAddons count:', filteredAddons.length, 'of', mappedAddons.length);

    // Sort by display_order (from product_types table)
    const sortedAddons = filteredAddons.sort((a, b) => {
      const orderA = a.display_order ?? 999; // Fallback for missing display_order
      const orderB = b.display_order ?? 999;
      return orderA - orderB;
    });

    debug.log('🔍 [AddonsModal] Sorted addons by display_order:', sortedAddons.map(a => `${a.name} (${a.display_order})`));

    return sortedAddons;
  }, [selectedProducts, addonsData, startDate]);

  const getRecommendedQuantity = (addonId) => {
    const addon = addonsData[addonId];
    if (!addon) return 1;

    const perBikeKeywords = ['insurance', 'helmet'];
    const sharedKeywords = ['gps', 'phone', 'child'];

    // Check addon name and category for keywords
    const addonName = (addon.name || '').toLowerCase();
    const addonCategory = (addon.category || '').toLowerCase();
    const searchString = `${addonName} ${addonCategory}`;

    // Check if addon name/category contains any per-bike keywords
    const isPerBike = perBikeKeywords.some(keyword => searchString.includes(keyword));
    if (isPerBike) {
      debug.log(`getRecommendedQuantity: ${addon.name} (${addonId}) is per-bike, returning ${totalBikes}`);
      return totalBikes;
    }

    // Check if addon name/category contains any shared keywords
    const isShared = sharedKeywords.some(keyword => searchString.includes(keyword));
    if (isShared) {
      debug.log(`getRecommendedQuantity: ${addon.name} (${addonId}) is shared, returning 1`);
      return 1;
    }

    debug.log(`getRecommendedQuantity: ${addon.name} (${addonId}) is default, returning 1`);
    return 1;
  };

  const updateAddonQuantity = (addonId, quantity) => {
    const addon = addonsData[addonId];

    // Check pool_quantity limit for pool-type inventory
    if (addon && addon.inventory_type === 'pool' && addon.pool_quantity) {
      if (quantity > addon.pool_quantity) {
        // Don't allow exceeding pool quantity
        debug.warn(`Cannot add more than ${addon.pool_quantity} ${addon.name}(s) - pool limit reached`);
        return;
      }
    }

    setSelectedAddons(prev => {
      if (quantity <= 0) {
        const newState = { ...prev };
        delete newState[addonId];
        return newState;
      }
      return { ...prev, [addonId]: quantity };
    });
  };

  const addAddonToCart = (addonId) => {
    debug.log('🛒 [AddonsModal] addAddonToCart called:', {
      addonId,
      addonExists: !!addonsData?.[addonId],
      addonName: addonsData?.[addonId]?.name,
      currentSelectedAddons: { ...selectedAddons },
      availableAddonsCount: availableAddons.length
    });

    const addon = addonsData[addonId];
    if (!addon) {
      debug.warn('⚠️ [AddonsModal] addAddonToCart: addon not found in addonsData!', {
        addonId,
        addonsDataKeys: Object.keys(addonsData || {})
      });
      return;
    }

    let recommendedQuantity = getRecommendedQuantity(addonId);

    // Cap recommended quantity at pool_quantity for pool items
    if (addon.inventory_type === 'pool' && addon.pool_quantity) {
      recommendedQuantity = Math.min(recommendedQuantity, addon.pool_quantity);
    }

    debug.log('🛒 [AddonsModal] Setting quantity:', { addonId, addonName: addon.name, recommendedQuantity });
    updateAddonQuantity(addonId, recommendedQuantity);
  };

  const getAddonQuantity = (addonId) => {
    return selectedAddons[addonId] || 0;
  };

  const getTotalPrice = () => {
    return Object.entries(selectedAddons).reduce((total, [addonId, quantity]) => {
      const addon = addonsData[addonId];
      const dynamicPrice = getAddonPrice(addon);
      return total + (dynamicPrice * quantity);
    }, 0);
  };

  const getTotalItemsCount = () => {
    return Object.values(selectedAddons).reduce((total, quantity) => total + quantity, 0);
  };

  const handleContinue = () => {
    debug.log('🛒 [AddonsModal] handleContinue called:', {
      selectedAddons: { ...selectedAddons },
      addonsDataKeys: Object.keys(addonsData || {}),
      availableAddonsCount: availableAddons.length
    });

    const fullAddonData = Object.entries(selectedAddons).map(([id, quantity]) => {
      const addon = addonsData[id];
      if (!addon) {
        debug.warn('⚠️ [AddonsModal] handleContinue: addon not found!', { id, addonsDataKeys: Object.keys(addonsData || {}) });
        return null;
      }
      // CRITICAL: Use duration-specific price from getAddonPrice(), not base price from database
      // This ensures insurance at 4.9€/day for 10 days = 49€ is sent to the booking API
      const calculatedPrice = getAddonPrice(addon);
      debug.log('🛒 [AddonsModal] Addon for checkout:', { id, name: addon.name, quantity, calculatedPrice });
      return {
        ...addon,
        quantity,
        price: calculatedPrice, // Override with duration-specific price
      };
    }).filter(Boolean);

    debug.log('🛒 [AddonsModal] Final addon data:', fullAddonData.length, 'addons, totalBikes:', totalBikes);
    onContinue(fullAddonData);
  };

  debug.log('🔍 [AddonsModal] Render check:', {
    isOpen,
    availableAddonsLength: availableAddons.length,
    willRender: isOpen && availableAddons.length > 0
  });

  if (availableAddons.length === 0) {
    debug.log('🔍 [AddonsModal] NOT rendering - availableAddons.length === 0');
    return null;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <m.div
          {...motion.modalOverlay}
          className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <m.div
            ref={focusTrapRef}
            {...motion.modalContent}
            className="bg-white w-full max-w-2xl max-h-[90vh] rounded-2xl shadow-xl overflow-hidden flex flex-col"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="addons-modal-title"
          >
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 id="addons-modal-title" className="text-2xl font-bold text-gray-900">{t('addons.title')}</h2>
                <p className="text-gray-600">{t('addons.subtitle')}</p>
              </div>
              <button
                onClick={onClose}
                className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
                aria-label={t('addons.closeModal') || 'Close add-ons modal'}
              >
                <SafeIcon icon={FiX} className="w-5 h-5 text-gray-600" aria-hidden="true" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {availableAddons.map(addon => {
                const quantity = getAddonQuantity(addon.id);
                const isSelected = quantity > 0;
                const recommendedQty = getRecommendedQuantity(addon.id);
                const iconKey = Object.keys(addonIcons).find(key => addon.id.includes(key)) || 'default';
                const hasPicture = addon.picture_url && addon.picture_url.trim() !== '';
                const isFeatured = addon.featured || addon.recommended;

                // Check if pool limit is reached
                const isPoolItem = addon.inventory_type === 'pool';
                const hasPoolLimit = isPoolItem && addon.pool_quantity;
                const atMaxCapacity = hasPoolLimit && quantity >= addon.pool_quantity;

                return (
                  <div
                    key={addon.id}
                    className={`flex items-start p-4 border-2 rounded-xl transition-all duration-200 relative ${
                      isSelected ? '' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    style={isSelected ? {
                      borderColor: theme.colors.primary.base,
                      backgroundColor: hexToRgba(theme.colors.primary.base, 0.05)
                    } : {}}
                  >
                    {/* Featured/Recommended Badge - Centered at top, overlapping border */}
                    {isFeatured && (
                      <div className="absolute left-1/2 -translate-x-1/2 -top-3 z-10">
                        <span className="bg-gradient-to-r from-orange-400 to-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                          {t('addons.recommended')}
                        </span>
                      </div>
                    )}

                    <div className="flex-shrink-0 mr-4">
                      {hasPicture ? (
                        <img
                          src={addon.picture_url}
                          alt={getProductTranslation(addon, 'product_name', currentLanguage, defaultLanguage) || addon.name}
                          className="w-12 h-12 md:w-20 md:h-20 rounded-lg object-cover"
                          style={isSelected ? { boxShadow: `0 0 0 2px ${theme.colors.primary.base}` } : {}}
                        />
                      ) : (
                        <div
                          className={`w-12 h-12 md:w-20 md:h-20 rounded-lg flex items-center justify-center ${
                            isSelected ? '' : 'bg-gray-100 text-gray-600'
                          }`}
                          style={isSelected ? { backgroundColor: theme.colors.primary.base, color: theme.colors.primary.contrastIcon } : {}}
                        >
                          <SafeIcon icon={addonIcons[iconKey] || FiPlusCircle} className="w-6 h-6 md:w-8 md:h-8" />
                        </div>
                      )}
                    </div>
                    <div className="flex-grow">
                      <h3 className="font-bold text-gray-900">{getProductTranslation(addon, 'product_name', currentLanguage, defaultLanguage) || addon.name}</h3>
                      <p className="text-sm text-gray-600 mb-3">{getProductTranslation(addon, 'description', currentLanguage, defaultLanguage) || addon.description}</p>
                      {/* Show pool limit info */}
                      {hasPoolLimit && (
                        <p className="text-xs text-gray-500 mb-2" translate="no">
                          {atMaxCapacity ? (
                            <span className="text-orange-600 font-medium">{t('addons.maximumAvailable')}: {addon.pool_quantity}</span>
                          ) : (
                            <span>{t('addons.available')}: {addon.pool_quantity - quantity} {t('addons.of')} {addon.pool_quantity}</span>
                          )}
                        </p>
                      )}
                      {/* translate="no": Chrome auto-translate mutates these DOM nodes,
                          but React also swaps them (Add button ↔ +/− controls).
                          Both touching the same node causes a white-screen crash. */}
                      <div className="flex items-center space-x-3" translate="no">
                        {quantity > 0 ? (
                          <>
                            <button
                              onClick={() => updateAddonQuantity(addon.id, quantity - 1)}
                              className="w-8 h-8 rounded-lg flex items-center justify-center transition-opacity active:scale-95 hover:opacity-80"
                              style={{ backgroundColor: theme.colors.primary.base, color: theme.colors.primary.contrastIcon }}
                              aria-label={`Decrease quantity of ${getProductTranslation(addon, 'product_name', currentLanguage, defaultLanguage) || addon.name}`}
                            >
                              <SafeIcon icon={FiMinus} className="w-4 h-4" aria-hidden="true" />
                            </button>
                            <span className="text-lg font-medium min-w-[32px] text-center" aria-label={`Quantity: ${quantity}`}>{quantity}</span>
                            <button
                              onClick={() => updateAddonQuantity(addon.id, quantity + 1)}
                              disabled={atMaxCapacity}
                              className="w-8 h-8 rounded-lg flex items-center justify-center transition-opacity active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80"
                              style={{ backgroundColor: atMaxCapacity ? '#9ca3af' : theme.colors.primary.base, color: atMaxCapacity ? '#ffffff' : theme.colors.primary.contrastIcon }}
                              aria-label={atMaxCapacity ? `Maximum ${addon.pool_quantity} available` : `Increase quantity of ${getProductTranslation(addon, 'product_name', currentLanguage, defaultLanguage) || addon.name}`}
                            >
                              <SafeIcon icon={FiPlus} className="w-4 h-4" aria-hidden="true" />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => addAddonToCart(addon.id)}
                            className="px-4 py-2 rounded-lg transition-opacity font-medium text-sm active:scale-95 hover:opacity-80"
                            style={{ backgroundColor: theme.colors.primary.base, color: theme.colors.primary.contrastText }}
                            aria-label={`Add ${getProductTranslation(addon, 'product_name', currentLanguage, defaultLanguage) || addon.name} to cart${recommendedQty > 1 ? `, ${recommendedQty} recommended` : ''}`}
                          >
                            {t('products.addToCart')}
                            {recommendedQty > 1 && (
                              <span className="ml-1 text-xs opacity-90">({recommendedQty})</span>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="ml-4 text-right" translate="no">
                      <div className="font-bold text-lg text-gray-900">
                        {formatCurrency(getAddonPrice(addon))}
                        {quantity > 1 && (
                          <div className="text-sm text-gray-600 font-normal">
                            {formatCurrency(getAddonPrice(addon) * quantity)} total
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-6 bg-gray-50 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div translate="no">
                  <p className="text-gray-600">
                    {t('addons.total', { count: getTotalItemsCount() })}:
                  </p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(getTotalPrice())}</p>
                </div>
                <button
                  onClick={handleContinue}
                  className="px-6 py-3 rounded-lg font-semibold transition-opacity duration-200 hover:opacity-90"
                  style={{ background: theme.colors.primary.gradient, color: theme.colors.primary.contrastText }}
                >
                  {t('products.checkout')}
                </button>
              </div>
            </div>
          </m.div>
        </m.div>
      )}
    </AnimatePresence>
  );
};

export default AddonsModal;